import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useSelector } from "react-redux";

import { useOnClickOutside } from "Utils/hooks";
import { getSemanticMaps } from "Slices/semanticMaps";

import { ZoneSelectorWrapperStyled } from "./styles";
import { ZoneSelectorButton } from "./ZoneSelectorButton";
import { ZoneSelectorDropdown } from "./ZoneSelectorDropdown";
import { isZoneSelectorEnabled } from "./utils";
import { useSemanticMapSelection } from "./hooks";

// ============================================================================
// Types
// ============================================================================

export interface ZoneSelectorRef {
  toggle: () => void;
}

export interface ZoneSelectorProps {
  onToggleDropdown?: () => void;
  isOpen?: boolean;
}

// ============================================================================
// Business Logic / Hooks
// ============================================================================

/**
 * Hook to manage dropdown state (internal or controlled)
 */
const useDropdownState = (isOpen?: boolean, onToggleDropdown?: () => void) => {
  const [internalState, setInternalState] = useState(false);

  // Use external isOpen prop if provided, otherwise use internal state
  const dropdownOpen = isOpen !== undefined ? isOpen : internalState;

  const handleToggle = useCallback(() => {
    if (isOpen === undefined) {
      setInternalState((prev) => !prev);
    } else {
      onToggleDropdown?.();
    }
  }, [isOpen, onToggleDropdown]);

  const handleClose = useCallback(() => {
    if (isOpen === undefined) {
      setInternalState(false);
    } else {
      onToggleDropdown?.();
    }
  }, [isOpen, onToggleDropdown]);

  return {
    dropdownOpen,
    handleToggle,
    handleClose,
  };
};

/**
 * Hook to handle dropdown visibility based on picker enabled state
 */
const useDropdownVisibility = (
  isPickerEnabled: boolean,
  handleClose: () => void
) => {
  useEffect(() => {
    if (!isPickerEnabled) {
      handleClose();
    }
  }, [isPickerEnabled, handleClose]);
};

// ============================================================================
// Main Component
// ============================================================================

const ZoneSelectorComponent = forwardRef<ZoneSelectorRef, ZoneSelectorProps>(
  ({ onToggleDropdown, isOpen }, ref) => {
    const semanticMaps = useSelector(getSemanticMaps);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const handleSelectedSemanticMap = useSemanticMapSelection();

    // Business Logic
    const isPickerEnabled = isZoneSelectorEnabled(semanticMaps.length);
    const { dropdownOpen, handleToggle, handleClose } = useDropdownState(
      isOpen,
      onToggleDropdown
    );

    // Expose toggle function via ref
    useImperativeHandle(ref, () => ({
      toggle: handleToggle,
    }));

    // Side Effects
    useOnClickOutside(wrapperRef, handleClose);
    useDropdownVisibility(isPickerEnabled, handleClose);

    // Event Handlers
    const handleSelectSemanticMap = useCallback(
      (semanticMapId: string) => {
        handleSelectedSemanticMap(semanticMapId);
        handleClose();
      },
      [handleSelectedSemanticMap, handleClose]
    );

    // Early return if picker is disabled
    if (!isPickerEnabled) {
      return null;
    }

    // Presentation
    return (
      <ZoneSelectorWrapperStyled ref={wrapperRef}>
        <ZoneSelectorButton isActive={dropdownOpen} onClick={handleToggle} />
        {dropdownOpen && (
          <ZoneSelectorDropdown
            semanticMaps={semanticMaps}
            onSelect={handleSelectSemanticMap}
          />
        )}
      </ZoneSelectorWrapperStyled>
    );
  }
);

ZoneSelectorComponent.displayName = "ZoneSelector";

export const ZoneSelector = memo(ZoneSelectorComponent);
